import AirDatepicker from 'air-datepicker';
import { utils } from '../tools/Utils.js';
import { Widget } from '../core/Widget.js';
import { toTimeOnly } from '../core/EncodeValues.js';
import { Formats, Loc } from '../locales/registry.js';
import { createPopper } from '@popperjs/core';

const RANGE_DASHES = "-‐‑–—−";
/** Путь до vendor-сборки стилей air-datepicker (см. work/vendor/air-datepicker.css). */
const AIR_DATEPICKER_VENDOR_CSS = '/src/vs/min/air-datepicker.css';
/** Вырезаем кастомные поля и нормализуем нативные флаги */
function toNativeOptions(o, rt) {
    if (!o)
        return {};
    const { outKey, ...native } = o;
    native.locale = rt.locale;
    // Нормализация флагов
    if (rt.onlyTimepicker) {
        native.timepicker = true;
        native.onlyTimepicker = true;
    }
    if (rt.range) {
        native.range = true;
        native.multipleDates = true;
        native.multipleDatesSeparator ??= rt.multipleDatesSeparator;
    }
    return native;
}
// Расширяем функциональность AirDatepicker через наследование
class AirDatepickerWidget extends Widget {
    constructor() {
        super(...arguments);
        this.isSyncing = false;
        this._calendarClicked = false;
        /** Флаг для onSelect, вызванного изменением времени */
        this._selectFromTimeChange = false;
        this.handlePopupMouseDown = (e) => {
            if (e.target.closest('.air-datepicker')) {
                this._calendarClicked = true;
                setTimeout(() => { this._calendarClicked = false; }, 100); // через 100 мс сбрасываем флаг
            }
        };
        /** Переменная для range режима, когда заполнено только 1 значение */
        this._rangeSingleSide = null;
    }
    /** Ключевой класс для поиска виджета */
    static _cssKey() {
        return 'im-datepicker';
    }
    /**
     * 2026-08-22: стили вынесены из основного бандла и грузятся при первом
     * создании датепикера. Без await: календарь рисуется по клику, к этому
     * моменту файл уже загружен. Для inline-режима возможен короткий показ
     * без стилей — если станет заметно, дождаться промиса именно там.
     */
    static ensureCss() {
        if (AirDatepickerWidget.cssRequested)
            return;
        AirDatepickerWidget.cssRequested = true;
        void utils.loadCss(AIR_DATEPICKER_VENDOR_CSS);
    }
    _create(options) {
        AirDatepickerWidget.ensureCss();
        // Помечаем что элемент использует только кастомные события change
        this.element._customChange = true;
        // 1) runtime — только нужные флаги/ключи
        this.rt = this.buildRuntime(options);
        // безопасно пробрасываем только нативные поля
        const opts = {
            timepicker: this.element.classList.contains('time'),
            minutesStep: options.minutesstep || 5,
            minHours: options.minhours || 0,
            maxHours: options.maxhours || 24,
            /*range: true,*/
            keyboardNav: false,
            // У autoClose есть баг при range (как-то странно выбираются даты)
            /*autoClose: true,*/
            ...toNativeOptions(options, this.rt),
            toggleSelected: false,
            inline: options.inline,
            dateFormat: options.dateformat,
            ...(options.headermonthonly && {
                navTitles: {
                    days: 'MMMM'
                }
            }),
            onSelect: ({ date, datepicker }) => {
                if (!this.rt.onlyTimepicker && !this._selectFromTimeChange) {
                    if (!this.rt.range) {
                        requestAnimationFrame(() => datepicker.hide());
                    }
                    else {
                        const arr = Array.isArray(date) ? date : (date ? [date] : []);
                        if (arr.length >= 2) {
                            requestAnimationFrame(() => datepicker.hide());
                        }
                    }
                }
                // событие пользователя — лучше тоже слать только когда диапазон завершён
                if (!this.rt.range) {
                    this._dispatchUserChange();
                }
                else {
                    const arr = Array.isArray(date) ? date : (date ? [date] : []);
                    if (arr.length === 0 || arr.length >= 2) {
                        this._dispatchUserChange();
                    }
                }
            },
            container: document.body,
            position({ $datepicker, $target, $pointer, done }) {
                let popper = createPopper($target, $datepicker, {
                    placement: 'bottom',
                    modifiers: [
                        {
                            name: 'flip',
                            options: {
                                padding: {
                                    top: 64
                                }
                            }
                        },
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 20]
                            }
                        },
                        {
                            name: 'arrow',
                            options: {
                                element: $pointer
                            }
                        }
                    ]
                });
                /*
                 Return function which will be called when `hide()` method is triggered,
                 it must necessarily call the `done()` function
                     to complete hiding process
                */
                return function completeHide() {
                    popper.destroy();
                    done();
                };
            }
        };
        this.datepicker = new AirDatepicker(this.element, opts);
        this._onTimeChangeHandler = (e) => {
            if (this.rt.onlyTimepicker)
                return;
            const target = e.target;
            if (!target?.closest('.air-datepicker--time, .air-datepicker-time'))
                return;
            this._selectFromTimeChange = true;
        };
        this._onTimeChangeEndHandler = () => {
            setTimeout(() => { this._selectFromTimeChange = false; }, 0);
        };
        this.datepicker.$datepicker.addEventListener('pointerdown', this._onTimeChangeHandler, true);
        this.datepicker.$datepicker.addEventListener('mousedown', this._onTimeChangeHandler, true);
        this.datepicker.$datepicker.addEventListener('input', this._onTimeChangeHandler, true);
        this.datepicker.$datepicker.addEventListener('change', this._onTimeChangeHandler, true);
        document.addEventListener('pointerup', this._onTimeChangeEndHandler, true);
        document.addEventListener('mouseup', this._onTimeChangeEndHandler, true);
        // blur
        this._onBlurHandler = this.onBlur.bind(this);
        this.element.addEventListener('blur', this._onBlurHandler);
        // keydown
        this._onKeyDownHandler = (e) => {
            if (e.key === 'Enter') {
                this.element.blur();
                return;
            }
            if (e.key === '/' || e.key === '\\') {
                e.preventDefault();
                e.stopImmediatePropagation();
                this._insertAtCursor('.');
                return;
            }
            if (e.key === '-') {
                const el = this.element;
                const value = el.value;
                const start = el.selectionStart ?? value.length;
                const end = el.selectionEnd ?? value.length;
                if (start !== end)
                    return;
                const before = value.slice(0, start);
                // если перед курсором пробел — уже формат " - "
                if (/\s$/.test(before))
                    return;
                // 🔹 вычисляем активный сегмент (1-я или 2-я дата)
                const activeChunk = (() => {
                    if (!this.rt.range)
                        return before;
                    const lastDashIndex = (() => {
                        for (let i = before.length - 1; i >= 0; i--) {
                            if (RANGE_DASHES.includes(before[i]))
                                return i;
                        }
                        return -1;
                    })();
                    if (lastDashIndex < 0)
                        return before;
                    return before.slice(lastDashIndex + 1).trimStart();
                })();
                // 🔹 если это внутри даты → заменяем на точку
                if (/^\d{2}$/.test(activeChunk) ||
                    /^\d{2}\.\d{2}$/.test(activeChunk) ||
                    /^\d{4}$/.test(activeChunk) ||
                    /^\d{4}\.\d{2}$/.test(activeChunk)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this._insertAtCursor('.');
                    return;
                }
                // 🔹 если это RANGE и уже есть одно тире → это второе тире = делаем " - "
                if (this.rt.range) {
                    const dashCount = [...before].filter(c => RANGE_DASHES.includes(c)).length;
                    const isDate1 = (/^\d{2}\.\d{2}.\d{4}$/.test(activeChunk));
                    if (dashCount === 0 && isDate1) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        el.value = value + ' - ';
                        el.dispatchEvent(new Event('input', { bubbles: true }));
                        return;
                    }
                }
                // иначе — даём обычному дефису вставиться
            }
        };
        this.element.addEventListener('keydown', this._onKeyDownHandler);
        // body listener
        document.body.addEventListener('mousedown', this.handlePopupMouseDown, true);
        this._lastValidValue = this.element.value.trim();
        if (this._lastValidValue) {
            if (this.rt.range) {
                const parts = this.splitRangeInput(this._lastValidValue);
                if (parts) {
                    const [a, b] = parts;
                    const start = this.parseDate(a);
                    const end = this.parseDate(b);
                    if (start && end) {
                        const origVal = this.element.value;
                        this.datepicker.selectDate([start, end], { silent: true });
                        this.datepicker.setViewDate(start);
                        // 2026-05-29: air-datepicker вызывает _setInputValue через setTimeout —
                        // восстанавливаем исходный текст, чтобы не перезаписать то, что ввёл пользователь
                        setTimeout(() => {
                            if (this.element.value !== origVal) {
                                this.element.value = origVal;
                                this._lastValidValue = origVal;
                            }
                        }, 0);
                    }
                }
            }
            else {
                const d = this.parseDate(this._lastValidValue);
                if (d) {
                    this.datepicker.selectDate(d, { silent: true });
                    this.datepicker.setViewDate(d);
                }
            }
        }
    }
    /**
     * Вставляет текст в позицию курсора, поддерживает выделение.
     * Используется для "подмены" символов при вводе с клавиатуры.
     */
    _insertAtCursor(text) {
        const el = this.element;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        el.value = el.value.slice(0, start) + text + el.value.slice(end);
        const pos = start + text.length;
        el.setSelectionRange(pos, pos);
        // Важно: сообщаем слушателям, что значение изменилось (маски/валидаторы/виджеты).
        el.dispatchEvent(new Event('input', { bubbles: true }));
    }
    /** Собрать runtime: outKey + фактические режимы */
    buildRuntime(options) {
        const key = this.element.dataset.key;
        const resolvedOut = (this.element.dataset.outkey || options?.outKey || `${key}__out`).trim();
        const onlyTimepicker = !!options?.onlyTimepicker || (!!options?.timepicker && !!options?.onlyTimepicker);
        const range = !!options?.range;
        const separator = options?.multipleDatesSeparator || ' — ';
        let locale;
        if (options?.locale) {
            locale = options.locale;
        }
        else {
            locale = {
                ...Loc.datepicker,
                dateFormat: Formats.dateFormat,
                timeFormat: Formats.timeFormat
            };
        }
        return { outKey: resolvedOut, onlyTimepicker, range, multipleDatesSeparator: separator, locale: locale };
    }
    /**
     * Освобождает ресурсы и снимает подписки.
     */
    _onDestroy() {
        if (this._onTimeChangeHandler) {
            this.datepicker.$datepicker.removeEventListener('pointerdown', this._onTimeChangeHandler, true);
            this.datepicker.$datepicker.removeEventListener('mousedown', this._onTimeChangeHandler, true);
            this.datepicker.$datepicker.removeEventListener('input', this._onTimeChangeHandler, true);
            this.datepicker.$datepicker.removeEventListener('change', this._onTimeChangeHandler, true);
        }
        if (this._onTimeChangeEndHandler) {
            document.removeEventListener('pointerup', this._onTimeChangeEndHandler, true);
            document.removeEventListener('mouseup', this._onTimeChangeEndHandler, true);
        }
        // Уничтожаем datepicker
        if (this.datepicker) {
            this.datepicker.destroy();
        }
        // Снимаем глобальные события
        document.body.removeEventListener('mousedown', this.handlePopupMouseDown, true);
        // Снимаем события input
        if (this._onBlurHandler) {
            this.element.removeEventListener('blur', this._onBlurHandler);
        }
        if (this._onKeyDownHandler) {
            this.element.removeEventListener('keydown', this._onKeyDownHandler);
        }
        // После снятия календаря форма снова должна принимать обычные события change поля. | 2026-10-02
        delete this.element._customChange;
    }
    // в классе
    splitRangeInput(value) {
        const rawSep = this.rt.multipleDatesSeparator ?? ' — ';
        const trimmed = rawSep.trim();
        // поддержим дефисы/тире/минус как разделитель диапазона
        const isDashSep = RANGE_DASHES.includes(trimmed);
        // делаем безопасный класс символов для RegExp на основе RANGE_DASHES
        const dashClass = RANGE_DASHES.replace(/-/g, '\\-');
        const escapedTrimmed = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const hasSpaces = rawSep !== trimmed;
        const re = isDashSep
            ? hasSpaces
                // Если сепаратор задан как " - " / " — ", требуем пробелы вокруг,
                // чтобы не резать ISO-дату по внутренним дефисам.
                ? new RegExp(`\\s+[${dashClass}]\\s+`)
                // Для одиночного dash-like символа без пробелов допускаем только символ,
                // который не стоит между цифрами.
                : new RegExp(`(?<!\\d)[${dashClass}](?!\\d)`)
            : new RegExp(`\\s*${escapedTrimmed}\\s*`);
        const parts = value.split(re).map(s => s.trim()).filter(Boolean);
        return parts.length === 2 ? [parts[0], parts[1]] : null;
    }
    /**
     * Обработка события blur — парсит введённую вручную строку даты, выставляет даты, если возможно
     * @param e - событие blur
     */
    onBlur(e) {
        if (this.isSyncing)
            return;
        if (this._calendarClicked)
            return;
        const value = e.target.value.trim();
        const wasValue = this._lastValidValue;
        try {
            if (!value) {
                this.datepicker.clear({ silent: true });
                this._lastValidValue = '';
                return;
            }
            let parsed = null;
            if (this.rt.range) {
                const parts = this.splitRangeInput(value);
                if (parts) {
                    const [a, b] = parts;
                    const start = this.parseDate(a);
                    const end = this.parseDate(b);
                    if (start && end)
                        parsed = [start, end];
                }
            }
            else {
                const date = this.parseDate(value);
                if (date)
                    parsed = date;
            }
            if (parsed) {
                this.isSyncing = true;
                const dates = Array.isArray(parsed) ? parsed : [parsed];
                if (Array.isArray(parsed)) {
                    this.datepicker.selectDate(parsed, { silent: true });
                    this.datepicker.setViewDate(parsed[0]);
                }
                else {
                    this.datepicker.selectDate(parsed, { silent: true });
                    this.datepicker.setViewDate(parsed);
                }
                // 2026-05-29: silent:true не обновляет element.value синхронно —
                // форматируем даты напрямую через публичный formatDate датепикера
                const dp = this.datepicker;
                const format = this.rt.locale?.dateFormat ?? dp?.locale?.dateFormat ?? 'yyyy-MM-dd';
                const fmt = (d) => {
                    if (typeof dp?.formatDate === 'function') {
                        return dp.formatDate(d, format);
                    }
                    return utils.formatDateByFormat(d, format);
                };
                const sep = this.rt.multipleDatesSeparator ?? ' — ';
                const formatted = dates.map(fmt).join(sep);
                this.element.value = formatted;
                this._lastValidValue = this.element.value.trim();
                setTimeout(() => { this.isSyncing = false; }, 0);
            }
            else {
                this.element.value = this._lastValidValue || '';
            }
        }
        finally {
            if (wasValue != this._lastValidValue)
                this._dispatchUserChange();
        }
    }
    /**
     * Парсинг даты из строки с учетом формата локали
     * @param str - строка с датой
     * @returns Date или null, если парсинг не удался
     *
     * Парсинг даты из строки с учетом формата локали и времени
     * Поддерживает строки вида:
     *  - dd.MM.yyyy
     *  - dd.MM.yyyy HH:mm
     *  - dd.MM.yyyy HH:mm:ss
     */
    parseDate(str) {
        const dateFormat = this.rt.locale?.dateFormat;
        const s = str.trim();
        // Вытаскиваем время (если есть)
        const m = s.match(/^(.*?)(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
        if (!m)
            return null;
        const [, datePart, hh, mm, ss] = m;
        // 1) парсим дату строго по локальному формату
        if (dateFormat) {
            const d = utils.parseDateByFormat(datePart.trim(), dateFormat);
            if (d instanceof Date && !isNaN(d.getTime())) {
                if (hh != null) {
                    d.setHours(Number(hh), Number(mm), ss ? Number(ss) : 0, 0);
                }
                return d;
            }
        }
        // 2) fallback — только если строка вида yyyy-mm-dd или yyyy.mm.dd (keydown заменяет дефисы на точки)
        // new Date("yyyy-mm-dd") даёт UTC-полночь, что согласовано с ожиданиями тестов и getValues()
        const isoMatch = s.match(/^(\d{4})[.\-](\d{2})[.\-](\d{2})$/);
        if (!isoMatch)
            return null;
        const isoDate = new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`);
        return isNaN(isoDate.getTime()) ? null : isoDate;
    }
    /**
     * Получить текущие выбранные даты или времени
     * @returns значение в соответствии с режимом
     */
    getValue() {
        if (this.rt.range)
            return undefined;
        let sel = [];
        if (this.datepicker.selectedDates.length > 0) {
            sel = this.datepicker.selectedDates;
        }
        else if (this._lastValidValue) {
            let date = this.parseDate(this._lastValidValue);
            if (date)
                sel.push(date);
        }
        const n = sel.length;
        if (n === 0)
            return null;
        if (this.rt.onlyTimepicker) {
            // режим «только время» → TimeOnly / TimeOnly[]
            if (n === 1)
                return toTimeOnly(sel[0]);
            return sel.map((d) => toTimeOnly(d));
        }
        else {
            // обычный режим даты → Date / Date[]
            if (n === 1)
                return new Date(sel[0].getTime()); // клон на всякий случай
            return sel.map((d) => new Date(d.getTime()));
        }
    }
    getValues() {
        const key = this.element.dataset.key;
        const result = {};
        if (this.rt.range) {
            const outKey = this.rt.outKey;
            const sel = this.datepicker?.selectedDates ?? [];
            const normDate = (d) => new Date(d.getTime());
            const normTime = (d) => toTimeOnly(d);
            if (sel.length === 0) {
                result[key] = null;
                result[outKey] = null;
            }
            else if (sel.length === 1) {
                const v = this.rt.onlyTimepicker ? normTime(sel[0]) : normDate(sel[0]);
                // <── учитываем, какую сторону мы считаем установленной
                if (this._rangeSingleSide === 'end') {
                    result[key] = null;
                    result[outKey] = v;
                }
                else {
                    // по умолчанию (или 'start')
                    result[key] = v;
                    result[outKey] = null;
                }
            }
            else {
                const a = sel[0], b = sel[1];
                if (this.rt.onlyTimepicker) {
                    result[key] = normTime(a);
                    result[outKey] = normTime(b);
                }
                else {
                    result[key] = normDate(a);
                    result[outKey] = normDate(b);
                }
            }
        }
        else {
            result[key] = this.getValue() ?? null;
        }
        return result;
    }
    setValues(values) {
        const key = this.element.dataset.key;
        const outKey = this.rt.outKey;
        if (!this.rt.range) {
            if (!(key in values))
                return false;
            this.setValue(values[key]);
            return true;
        }
        const sel = (this.datepicker?.selectedDates ?? []).slice(0, 2);
        let start = sel[0] ?? null;
        let end = sel[1] ?? null;
        if (key in values) {
            const v = values[key];
            const first = Array.isArray(v) ? v[0] : v;
            start = this.coerceToDateStrict(first);
        }
        if (outKey in values) {
            const v = values[outKey];
            const first = Array.isArray(v) ? v[0] : v;
            end = this.coerceToDateStrict(first);
        }
        this.isSyncing = true;
        if (!start && !end) {
            this.datepicker.clear({ silent: true });
            this.element.value = '';
            this._lastValidValue = '';
            this._rangeSingleSide = null;
            this.isSyncing = false;
            return true;
        }
        const next = [];
        if (start)
            next.push(start);
        if (end)
            next.push(end);
        if (next.length === 2 && next[0].getTime() > next[1].getTime()) {
            [next[0], next[1]] = [next[1], next[0]];
        }
        this.datepicker.selectDate(next, { silent: true });
        this.datepicker.setViewDate(next[0] ?? new Date());
        this._lastValidValue = this.element.value.trim();
        // <── проставляем метку, если одна точка
        this._rangeSingleSide = next.length === 1
            ? (start ? 'start' : 'end')
            : null;
        this.isSyncing = false;
        return true;
    }
    setValue(val) {
        if (this.rt.range)
            return false;
        // Приводим к одному элементу (если пришёл массив)
        const first = Array.isArray(val) ? (val.length ? val[0] : undefined) : val;
        const d = this.coerceToDateStrict(first);
        // Очистка, если ничего валидного не пришло
        if (!d) {
            this.isSyncing = true;
            this.datepicker.clear({ silent: true });
            this.element.value = '';
            this._lastValidValue = '';
            this.isSyncing = false;
            return true;
        }
        // Применяем
        this.isSyncing = true;
        this.datepicker.selectDate(d, { silent: true });
        this.datepicker.setViewDate(d);
        this._lastValidValue = this.element.value.trim();
        this.isSyncing = false;
        return true;
    }
    hasValue() {
        return (this.datepicker.selectedDates?.length ?? 0) > 0;
    }
    /** Построить Date из TimeOnly на базе "сегодня" (дата-носитель игнорируется логикой onlyTimepicker) */
    fromTimeOnly(t, base) {
        const b = base ? new Date(base.getTime()) : new Date();
        b.setHours(t.hours, t.minutes, t.seconds ?? 0);
        return b;
    }
    /** true, если значение имеет форму TimeOnly */
    isTimeOnly(x) {
        return !!x
            && typeof x.hours === 'number'
            && typeof x.minutes === 'number'
            && (typeof x.seconds === 'number' || x.seconds === undefined);
    }
    /** Строго привести вход к Date с учётом onlyTimepicker и ISO-строк */
    coerceToDateStrict(val) {
        if (val == null)
            return null;
        // TimeOnly
        if (this.isTimeOnly(val)) {
            return this.fromTimeOnly(val);
        }
        // Date
        if (val instanceof Date) {
            if (isNaN(val.getTime()))
                return null;
            if (this.rt.onlyTimepicker) {
                // берём только время из входной даты
                return this.fromTimeOnly({
                    hours: val.getHours(),
                    minutes: val.getMinutes(),
                    seconds: val.getSeconds(),
                });
            }
            return new Date(val.getTime());
        }
        // string (ISO предпочтительно)
        const s = String(val).trim();
        if (!s)
            return null;
        // 1) пробуем локальный парсер (если задан формат)
        const byFormat = this.parseDate(s);
        if (byFormat) {
            if (this.rt.onlyTimepicker) {
                // из локально распарсенной даты возьмём только время
                return this.fromTimeOnly({
                    hours: byFormat.getHours(),
                    minutes: byFormat.getMinutes(),
                    seconds: byFormat.getSeconds(),
                });
            }
            return byFormat;
        }
        // 2) ISO / native Date
        const iso = new Date(s);
        if (!isNaN(iso.getTime())) {
            if (this.rt.onlyTimepicker) {
                return this.fromTimeOnly({
                    hours: iso.getHours(),
                    minutes: iso.getMinutes(),
                    seconds: iso.getSeconds(),
                });
            }
            return iso;
        }
        return null;
    }
    /**
     * Получить текущие выбранные даты в ISO-формате, разделённые запятой
     * @returns строка ISO-дат или null, если ничего не выбрано
     */
    getValueISO() {
        return this.datepicker.selectedDates.length > 0 ?
            this.datepicker.selectedDates.map((d) => d.toISOString()).join(', ')
            : null;
    }
    /**
     * Обновление опций календаря
     * @param options
     */
    updateOptions(options) {
        this.datepicker.update(options);
    }
}
/** Имя виджета */
AirDatepickerWidget._alias = "Date";
/** Стили подгружены — грузим один раз на страницу, а не на каждый виджет. */
AirDatepickerWidget.cssRequested = false;

export { AIR_DATEPICKER_VENDOR_CSS, AirDatepickerWidget, RANGE_DASHES };
